import { Box, Button, Icon, Text, Tooltip } from "@chakra-ui/react";
import { MdAirplay, MdImage, MdMovie, MdOutlineWebStories } from "react-icons/md";
import { RiArrowDownLine, RiArrowUpDownLine, RiArrowUpLine } from "react-icons/ri";

// Shared helpers for ContentSelection and BroadcastSelection.

// One shared empty list, so `selected || EMPTY_LIST` keeps the same reference between renders.
export const EMPTY_LIST = []

export const sameList = (a, b) => a.length === b.length && a.every((value, i) => value === b[i])

// The same content/broadcast can be in a playlist several times, so every playlist
// entry gets its own `uid`, created once when the entry is added (or loaded) and kept
// while entries are reordered. It is used as the React key and the dnd-kit id, so
// reordering MOVES the existing DOM nodes instead of re-creating them. Re-creating
// them is what reset the scroll position and dropped a browser Ctrl+F match.
//
// `added` marks an entry the user added while editing (the list is not saved yet). It is
// only set by the "add" buttons: entries that come from the saved list are not added, and
// the form is closed after saving, so the mark never outlives the editing session.
let uidCounter = 0
export const makeEntries = (ids, added = false) => ids.map((id) => ({ uid: 'entry-' + uidCounter++, id, added }))

// Background of a selected item that was added during this edit (the saved ones are white).
export const NEW_ENTRY_BG = 'brand.100'
export const NEW_ENTRY_TITLE = 'Added just now, not saved yet'

export const countAdded = (entries) => entries.filter(e => e.added).length

// Sort toggle: sortKey is "field" (ascending) or "-field" (descending).
export function SortButton({ label, field, sortKey, onSort }) {
  const active = sortKey.includes(field)
  const arrow = active ? (sortKey.startsWith('-') ? RiArrowDownLine : RiArrowUpLine) : RiArrowUpDownLine
  return (
    <Button colorScheme='brand' onClick={() => onSort(field)} variant={active ? 'solid' : 'ghost'} size='xs'>
      {label} <Icon as={arrow} color='brand' w={3} h={3} />
    </Button>
  )
}

// Position of an item in the selected playlist (1, 2, 3 ...).
export function SelectionNumber({ value }) {
  return (
    <Text fontSize='sm' fontWeight='bold' color='brand.500' minW={6} textAlign='right'>{value}.</Text>
  )
}

// Green dot on content that is played in at least one broadcast. `names` (optional) are the
// broadcasts, shown when the mouse rests on the dot.
export function UsedDot({ count, names }) {
  if (!(count > 0)) return null
  const label = count === 1 ? 'In 1 broadcast' : `In ${count} broadcasts`
  return (
    <Tooltip label={names && names.length > 0 ? `${label}: ${names.join(', ')}` : label}>
      <Box as='span' display='inline-block' flexShrink={0} w='10px' h='10px' borderRadius='full' bg='green.400' aria-label={label} />
    </Tooltip>
  )
}

// Icon showing whether a content item is a video or an image.
export function ContentTypeIcon({ type }) {
  const isImage = type === 'image'
  return (
    <Tooltip label={isImage ? 'Image' : 'Video'}>
      <Box as='span' display='inline-flex'>
        <Icon as={isImage ? MdImage : MdMovie} w={5} h={5} color={isImage ? 'green.500' : 'blue.500'} />
      </Box>
    </Tooltip>
  )
}

// Icon showing whether a broadcast is regular or composite.
export function BroadcastTypeIcon({ composite }) {
  return (
    <Tooltip label={composite ? 'Composite broadcast' : 'Regular broadcast'}>
      <Box as='span' display='inline-flex'>
        <Icon as={composite ? MdOutlineWebStories : MdAirplay} w={5} h={5} color='brand.500' />
      </Box>
    </Tooltip>
  )
}
